import { tvpil } from '../../../data/compat/tvpil';
import { KalturaAssetDto } from '../../composed';
import { KalturaAssetOrderBy } from '../../enums';
import {
  createAssetMetaOrTagGroupBy,
  createChannelFilter,
  createDynamicOrderBy,
  createListGroupsRepresentativesFilter,
  createPager,
  createSearchAssetFilter,
} from '../../factories';
import { fetchAssetGroupRepresentativeList, fetchAssetList } from '../../services';

const assetOrderBy: KalturaAssetOrderBy[] = [
  'RELEVANCY_DESC',
  'NAME_ASC',
  'NAME_DESC',
  'VIEWS_DESC',
  'RATINGS_DESC',
  'VOTES_DESC',
  'START_DATE_DESC',
  'START_DATE_ASC',
  'LIKES_DESC',
  'CREATE_DATE_ASC',
  'CREATE_DATE_DESC',
];

export type FetchSearchResults = {
  adultCategoryReferenceId?: string;
  collectionFilteringKsql?: string;
  collectionId?: number;
  isGroupByEnabled: boolean;
  groupBy: string;
  groupByPolicy?: object;
  groupByRepresentativePolicy?: object;
  groupByUnmatchedItemsPolicy?: object;
  kSql: string;
  orderByPolicy?: object;
  orderBy?: string;
  pageIndex: number;
  pageSize: number;
};

/**
 * @see https://kaltura.atlassian.net/wiki/spaces/KUXDEV/pages/4117692648/Search+API+with+grouping+capabilities#Search-Request
 */
export function fetchSearchResults({
  adultCategoryReferenceId,
  collectionFilteringKsql,
  collectionId,
  isGroupByEnabled,
  groupBy,
  groupByPolicy,
  groupByRepresentativePolicy,
  groupByUnmatchedItemsPolicy,
  kSql,
  orderBy,
  orderByPolicy,
  pageIndex,
  pageSize,
}: FetchSearchResults) {
  const pager = createPager({ pageIndex, pageSize });

  const resultKsql = getKsql(kSql, adultCategoryReferenceId);

  if (isGroupByEnabled) {
    return fetchAssetGroupRepresentativeList<KalturaAssetDto>({
      filter: createListGroupsRepresentativesFilter({
        kSql: tvpil.addKsql(resultKsql, collectionFilteringKsql ?? ''),
      }),
      ...groupByPolicy,
      ...groupByRepresentativePolicy,
      ...groupByUnmatchedItemsPolicy,
      ...orderByPolicy,
      pager,
    });
  }

  if (collectionId) {
    return fetchAssetList<KalturaAssetDto>({
      filter: createChannelFilter({
        idEqual: collectionId,
        kSql: resultKsql,
        ...(groupBy && { groupBy: [createAssetMetaOrTagGroupBy({ value: groupBy })] }),
        ...(orderBy && getOrderBy(orderBy)),
      }),
      pager,
    });
  }

  return fetchAssetList<KalturaAssetDto>({
    filter: createSearchAssetFilter({
      kSql: tvpil.addKsql(resultKsql, collectionFilteringKsql ?? ''),
      ...orderByPolicy,
    }),
    pager,
  });
}

function getOrderBy(orderBy: string) {
  if (assetOrderBy.includes(orderBy.toUpperCase() as KalturaAssetOrderBy)) {
    return { orderBy: orderBy as KalturaAssetOrderBy };
  }

  if (orderBy.toLowerCase() === 'name') {
    return { orderBy: 'NAME_ASC' as KalturaAssetOrderBy };
  }

  return { dynamicOrderBy: createDynamicOrderBy({ name: orderBy, orderBy: 'META_ASC' }) };
}

function getKsql(kSql: string, adultCategoryReferenceId?: string) {
  if (!adultCategoryReferenceId) {
    return kSql;
  }

  return tvpil.addKsql(kSql, `in_app_link!='${adultCategoryReferenceId}'`);
}
